import { Dispatch } from 'redux';
import queryString from 'query-string';

import { IState } from '../reducers';
import { Http } from '../tools/http';
import * as actionTypes from './actionTypes/profileTypes';
import { getOnboardingProfileScore } from './onboardingActions';
import { action } from '../tools/reduxTools';
import { IRelation, IFullUserProfile, IUserQuerySearchResult, ISingleMandeHolder, IUserRegistrationValidation } from '../entities/IProfile';
import { objectKeysToCamelCase } from '../tools/objectTools';
import { delay } from '../tools/generalTools';
import { UserProfileBody } from '../entities/IDeclaration';
import { ISearchResponse, IUserBrief, ILegalEntityBrief } from '../entities/IGlobal';
import { ISinglePoa } from '../entities/IPOA';
import { ISearch } from '../entities/ISearch';
import { ISearchResults } from '../entities/IFilters';
import * as actionSearchTypes from './actionTypes/searchTypes';
import { EUserPrivacy } from '../entities/IRoleManager';

export const getDirectoryUers = (search: string, page: number, size: number, filter: any) => action<Promise<ISearch>>((dispatch, getState, http: Http) => {
    const { userLanguage } = getState().language;
    const query = queryString.stringify({
        page: page,
        size: size,
        lcid: userLanguage
    });
    const body = {
        departments: filter.departments,
        teams: filter.teams,
        languages: filter.languages,
        barAdmissions: filter.barAdmissions,
        expertises: filter.expertises,
        competences: filter.competences,
        businessSectors: filter.businessSectors,
        countries: filter.countries,
        talents: filter.talents,
        roleFunctions: filter.roleFunctions,
        roleCategories: filter.roleCategories,
        firstReferentials: filter.firstReferentials,
        secondReferentials: filter.secondReferentials,
        thirdReferentials: filter.thirdReferentials,
        legalEntities: filter.cabinet,
        search
    };
    const url = `/directory-users/search?${query}`;
    return http.profilesApiPost(url, body)
        .then((searchResult: ISearch) => {
            dispatch({
                type: actionSearchTypes.GET_SEARCH_RESULT,
                searchResult: {
                    items: searchResult.items,
                    facets: searchResult.facets,
                    count: searchResult.count
                }
            });
            return searchResult;
        }).catch(err => {
            throw new Error(err);
        });
});

export const checkFinalRegistrationRule = () => action<Promise<any>>((dispatch: Dispatch, getState: () => IState, http: Http) => {
    return http.profilesApiGet('/users/final-registration/is-active');
});

export const getCurrentUserProfileNew = () => action<Promise<IFullUserProfile>>((dispatch: Dispatch, getState: () => IState, http: Http) => {
    dispatch({ type: actionTypes.GET_CURRENT_USER_PROFILE_REQUESTED });

    function fetchUserProfileWithRetryPromise(attempt = 1, maxAttempts = 3, delayMs = 1000) {
        return http.profilesApiGet('/users/me/full')
            .then((currentUserProfile: IFullUserProfile) => {
                dispatch({
                    type: actionTypes.GET_CURRENT_USER_PROFILE_COMPLETED,
                    currentUserProfile: objectKeysToCamelCase<IFullUserProfile>(currentUserProfile)
                });
                dispatch(getOnboardingProfileScore(objectKeysToCamelCase<IFullUserProfile>(currentUserProfile)));
                return currentUserProfile;
            })
            .catch((err: Error) => {
                if (attempt < maxAttempts) {
                    return delay(delayMs).then(() =>
                        fetchUserProfileWithRetryPromise(attempt + 1, maxAttempts, delayMs)
                    );
                } else {
                    dispatch({
                        type: actionTypes.GET_CURRENT_USER_PROFILE_FAILED
                    });
                    return Promise.reject(err);
                }
            });
    }

    return fetchUserProfileWithRetryPromise();
});

export const getExploreDirectoryUsers = (page: number = 0, size: number = 48, body, loadSingleData: boolean) => action<Promise<ISearchResults>>((dispatch, getState, http) => {
    const { userLanguage } = getState().language;
    const query = queryString.stringify({
        page: page,
        size: size,
        lcid: userLanguage
    });
    return http.profilesApiPost(`/directory-users/search?${query}`, body);
});

export const getProfileById = (id: string, lcid: number, getExternalProfileData: boolean): Promise<IFullUserProfile> => ((dispatch: Dispatch, getState, http: Http) => {
    const { userLanguage } = getState().language;
    return http.profilesApiGet(`/users/${id}/full?lcid=${lcid || userLanguage || 1033}&getExternalProfileData=${getExternalProfileData}`);
}) as any;

export const getUserProfileByQuery = (body: UserProfileBody, page: number = 0, size: number = 10) => action<Promise<IUserQuerySearchResult>, IState>((dispatch: Dispatch, getState, http: Http) => {
    const { userLanguage } = getState().language;
    const query = queryString.stringify({ page, size, lcid: userLanguage });

    return http.profilesApiPost(`/users/search?${query}`, body, undefined);
});

export interface ICreateSingleUserPayload {
    firstName: string;
    lastName: string;
    email: string | null;
}

export const createSingleUser = (body: ICreateSingleUserPayload) => action<Promise<IUserBrief>, IState>((dispatch, getState, http) => {
    const { userLanguage } = getState().language;
    return http.profilesApiPost(`/users?lcid=${userLanguage}`, body);
});

export const getOtherUserPolicy = (userId: string = ''): Promise<any> => ((dispatch: Dispatch, getState, http: Http) => {
    return http.paymentApiGet(`/User/${userId}/policies`);
}) as any;

export const changePassword = (userB2Cid: string, password: string): Promise<string> => ((dispatch: Dispatch, getState: () => IState, http: Http) => {
    return http.profilesApiPatch(`/users/change-password`, {userB2CId: userB2Cid, newPassword: password});
}) as any;

export const addProfilePicture = (userId: string, file: Blob) => ((dispatch: Dispatch, getState: () => IState, http: Http) => {
    const form = new FormData();
    form.append('file', file); // not a File instance so no encoding needed for the filename here

    return http.commonApiPost(`/images/User/${userId}`, form);
}) as any;

export const getAllRoleCategories = (lcid: number = 1033) => ((dispatch: Dispatch, getState: () => IState, http: Http) => {
    return http.apiGet(`/GetAllRoleCategories?lcid=${lcid}`);
}) as any;

export const getAllRelationsByCategory = (category: string, lcid: number = 1033): Promise<IRelation[]> => ((dispatch: Dispatch, getState: () => IState, http: Http) => {
    return http.apiGet(`/GetAllRelationsByCategory?categoryEnum=${category}&lcid=${lcid}`);
}) as any;

export const getReferentialListByName = (referentialName: string, name: string, size: number = 10, page: number = 1, lcid: number = 1033) => ((dispatch: Dispatch, getState: () => IState, http: Http) => {
    return http.apiGet(`/Get${referentialName}ListByName?${(!!name ? name.trim() !== '' : '') && `name=${encodeURIComponent(name)}&`}size=${size}&page=${page}&lcid=${lcid}`);
}) as any;

export const sendVerificationCode = (email: string) => ((dispatch: Dispatch, getState: () => IState, http: Http) => {
    return http.profilesApiPost(`/users/me/settings/email/generate-code?email=${email}`);
}) as any;

export const checkUsedEmail = (email: string) => ((dispatch: Dispatch, getState: () => IState, http: Http) => {
    return http.apiGet(`/users/me/settings/change-email/is-used?email=${email}`);
}) as any;

export const changeUserEmail = (oldEmail: string, newEmail: string, verificationKey: string) => action<Promise<{}>>((dispatch: Dispatch, getState: () => IState, http: Http) => {
    const body = {
        oldEmail,
        newEmail,
        verificationKey
    };
    return http.profilesApiPut(`/users/me/settings/email`, body);
});

export const updateProfile = (userId: string, body: IFullUserProfile) => action<Promise<IFullUserProfile>>((dispatch, getState, http) => {
    const { userLanguage } = getState().language;
    body.firstName = body.firstName.trim();
    body.lastName = body.lastName.trim();
    return http.profilesApiPut(`/users/${userId}?lcid=${userLanguage}`, body);
});

export const getProfileMandates = (userId: string, page: number, size: number = 40, timeout?: number) => action<Promise<ISearchResponse<ISingleMandeHolder>>>((dispatch, getState, http) => {
    const { userLanguage } = getState().language;
    return http.profilesApiGet(`/users/${userId}/corporate-mandates?lcid=${userLanguage}&page=${page}&size=${size}`, undefined, undefined, timeout);
});

export const getMandatForExport = (userId: string, timeout?: number) => action<Promise<ISearchResponse<ISingleMandeHolder>>>((dispatch, getState, http) => {
    const { userLanguage } = getState().language;
    const body = {
        lcid: userLanguage
    };
    return http.profilesApiPost(`/users/${userId}/corporate-mandates/as-datatable?page=0&size=100`, body, undefined, timeout);
});

export const getProfilePoa = (userId: string, timeout?: number) => action<Promise<ISearchResponse<ISinglePoa>>>((dispatch, getState, http) => {
    const { userLanguage } = getState().language;
    return http.profilesApiGet(`/users/${userId}/poa-mandates/as-delegated?lcid=${userLanguage}&size=999999`, undefined, undefined, timeout);
});

export const sendValidation = (userId: string, body: IUserRegistrationValidation) => action<Promise<ISearchResponse<ISinglePoa>>>((dispatch, getState, http) => {
    return http.profilesApiPut(`/users/${userId}/validate-registration`, body);
});

export const changeUserPrivacy = (userId: string, body: EUserPrivacy) => action<Promise<ISearchResponse<ISinglePoa>>>((dispatch, getState, http) => {
    return http.profilesApiPut(`/users/${userId}/privacy-settings`, {privacy: body});
});

export const checkUserInHrApi = (query: string) => action<Promise<ISearchResponse<IUserBrief>>>((dispatch, getState, http) => {
    return http.profilesApiGet(`/users/hr/search?query=${query}`);
});

export const updateUserProfileLEOrEmail = (userId: string, body: { legalEntityId: string, email: string }) => action<Promise<ISearchResponse<IUserBrief>>>((dispatch, getState, http) => {
    const { userLanguage } = getState().language;
    return http.profilesApiPut(`/users/${userId}/update-empty-fields?lcid=${userLanguage}`, body);
});

export interface ISubstitutionUser {
    cabinet: string;
    emailContact: string;
    firstName: string;
    groups: string[];
    id: string;
    jobTitle: string;
    lastName: string;
    legalEntityBrief?: ILegalEntityBrief;
    organizationsGroups: string[];
    picture: string;
    upn: string;
}

export interface ICreateSubstitutionPayload {
    endDate: string;
    resourceType: number;
    startDate: string;
    status: number;
    substitute: ISubstitutionUser;
    substitutor: ISubstitutionUser;
}

export interface IUpdateSubstitutionPayload {
    endDate: string;
    id: string;
    resourceType: number;
    startDate: string;
    status: number;
    substitute: ISubstitutionUser;
    substitutor: ISubstitutionUser;
}

export const createSubstitution = (payload: ICreateSubstitutionPayload) => action<Promise<ICreateSubstitutionPayload>>((dispatch, getState, http) => {
    return http.profilesApiPost('/substitutions', payload);
});

export const updateSubstitution = (substitutionId: string, payload: IUpdateSubstitutionPayload) => action<Promise<IUpdateSubstitutionPayload>>((dispatch, getState, http) => {
    return http.profilesApiPut(`/substitutions/${substitutionId}`, payload);
});

export const cancelSubstitution = (substitutionId: string) => action<Promise<void>>((dispatch, getState, http) => {
    return http.profilesApiPost(`/substitutions/${substitutionId}/cancel`, {});
});
